import React, { useContext, useEffect, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../../utils/apiFetch'
import apiPath from '../../utils/pathObj'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import EmployeeList from './EmployeeList'
import CandidateAiDrawer from './CandidateAiDrawer.js'
import { useLocation, useNavigate } from 'react-router-dom'
import CustomSelect from 'components/reusable/CustomSelect'
import { BsRobot, BsSendFill } from 'react-icons/bs'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'

function EmployeeRecommendation () {
  const { t } = useTranslation()
  const location = useLocation()
  const { logoutUser, user, updatePageName } = useContext(AuthContext)
  const navigate = useNavigate()

  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })

  const [recommendations, setRecommendations] = useState([])
  const [jobs, setJobs] = useState([])
  const [selectedJob, setSelectedJob] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)

  const [expandedJobGroups, setExpandedJobGroups] = useState({})

  // ── AI search state ──
  const [aiQuery, setAiQuery] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiSummary, setAiSummary] = useState(null)
  const [aiClarification, setAiClarification] = useState(null)
  const [aiActive, setAiActive] = useState(false)

  // ── Drawer state ──
  const [drawerState, setDrawerState] = useState(null)
  const [bulkResult, setBulkResult] = useState(null);

  const [sortOptions] = useState([
    { value: 'a_z', label: 'A-Z' },
    { value: 'z_a', label: 'Z-A' }
  ])

  const [columnOptions] = useState([
    { value: 'employee_name', label: t('NAME') },
    { value: 'match_score', label: t('MATCH_SCORE') }
  ])

  const [filterData, setFilterData] = useState({
    searchkey: '',
    isReset: false,
    isFilter: false
  })

  const [sort, setSort] = useState({
    sortBy: 'matchScore',
    sortType: 'desc',
    sortColumn: 'match_score'
  })

  const getSortByField = useCallback(() => {
    switch (sort.sortColumn) {
      case 'employee_name':
        return 'name'
      case 'match_score':
        return 'matchScore'
      case 'createdAt':
        return 'createdAt'
      default:
        return 'matchScore'
    }
  }, [sort.sortColumn])

const fetchCompanyJobs = useCallback(async () => {
  try {
    const path = apiPath.jobsForFilter
    const payload = { status: 'active', adminStatus: 'accepted' }
    const result = await apiGet(path, payload)
    if (result?.status === 200 && result?.data?.success) {
      // New endpoint returns a plain array, not { docs: [...] }
      setJobs(result.data.results || [])
    }
  } catch (error) {
    console.error('Error fetching jobs:', error)
  }
}, [])

  const getRecommendations = useCallback(async () => {
    try {
      setLoading(true)
      const payload = {
        page,
        pageSize,
        jobId: selectedJob || undefined,
        keyword: filterData.searchkey,
        sortBy: getSortByField(),
        sortType: sort.sortType
      }

      const path = apiPath.employeeRecommendation
      const result = await apiGet(path, payload)

      if (result?.status === 200) {
        const response = result?.data?.results
        const resultStatus = result?.data?.success

        if (resultStatus) {
          const jobGroups = response.data || []
          setRecommendations(jobGroups)

          const initialExpandedState = {}
          jobGroups.forEach(group => {
            initialExpandedState[group.jobId] = false
          })
          setExpandedJobGroups(initialExpandedState)

          setPaginationObj(prev => ({
            ...prev,
            page: response.currentPage,
            pageCount: response.totalPages,
            perPageItem: response.data?.length || 0,
            totalItems: response.totalRecommendations
          }))
        }
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error)
      setRecommendations([])
      setPaginationObj({})
      if (error.response?.status === 401 || error.response?.status === 409) {
        logoutUser()
      }
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, selectedJob, filterData.searchkey, sort.sortType, getSortByField, logoutUser])

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const handleResetSate = () => {
    setSort({ sortBy: 'matchScore', sortType: 'desc', sortColumn: 'match_score' })
  }

  const handleJobChange = e => {
    const jobId = e.target.value
    setSelectedJob(jobId)
    setPage(1)
  }

  const toggleJobGroup = (jobId) => {
    setExpandedJobGroups(prev => ({ ...prev, [jobId]: !prev[jobId] }))
  }

  const toggleAllJobGroups = () => {
    const allExpanded = Object.values(expandedJobGroups).every(Boolean)
    const newState = {}
    recommendations.forEach(group => { newState[group.jobId] = !allExpanded })
    setExpandedJobGroups(newState)
  }

  useEffect(() => {
    updatePageName(t('EMPLOYEE_RECOMMENDATION'))
    fetchCompanyJobs()
    if (location.state) {
      setSelectedJob(location.state)
    }
  }, [])

  useEffect(() => {
    if (aiActive) return
    const timer = setTimeout(() => { getRecommendations() }, 300)
    return () => clearTimeout(timer)
  }, [page, selectedJob, filterData.searchkey, sort.sortType, getSortByField, getRecommendations, aiActive])

  useEffect(() => {
    const timeoutId = setTimeout(() => { setDebouncedSearchTerm(searchTerm) }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    if (searchTerm || !filterData?.isReset) {
      setFilterData(prev => ({ ...prev, isReset: false, searchkey: debouncedSearchTerm, isFilter: !!debouncedSearchTerm }))
      setPage(1)
    }
  }, [debouncedSearchTerm])

  const handleReset = () => {
    if (location.state) handleResetSate()
    setFilterData({ searchkey: '', isReset: true, isFilter: false })
    setSelectedJob('')
    setSort({ sortBy: 'matchScore', sortType: 'desc', sortColumn: 'match_score' })
    setPage(1)
    setSearchTerm('')
    clearAiSearch()
  }

  const handleSortOrderChange = e => {
    const orderValue = e.target.value
    setSort(prev => ({ ...prev, sortType: orderValue === 'a_z' ? 'asc' : 'desc' }))
    setPage(1)
  }

  const handleSortColumnChange = e => {
    const columnValue = e.target.value
    setSort(prev => ({ ...prev, sortColumn: columnValue, sortType: columnValue === 'match_score' ? 'desc' : 'asc' }))
    setPage(1)
  }

  const handleSearchTerm = e => {
    if (location.state) handleResetSate()
    setSearchTerm(e.target.value)
  }

  // ── AI search handlers ──
  const clearAiSearch = () => {
    setAiActive(false)
    setAiSummary(null)
    setAiClarification(null)
    setAiQuery('')
  }

  // Main AI search — sends query as-is. If the backend decides it needs
  // clarification, it returns options and the user picks one via
  // runAiSearchWithJob below.
  const runAiSearch = async (queryText) => {
    const trimmed = (queryText ?? aiQuery).trim()
    if (!trimmed || aiLoading) return

    setAiLoading(true)
    setAiClarification(null)
    setRecommendations([])
    setAiSummary(null)

    try {
      const result = await apiPost(apiPath.aiAssistantSearch, {
        query: trimmed,
        jobId: selectedJob || undefined
      })

      if (result?.status === 200 && result?.data?.success) {
        const { summary, options, data } = result.data.results

        if (options && options.length > 0) {
          // Clarification: show summary ONCE (in the gray box) + chips below.
          setAiSummary(summary)
          setAiClarification({ options })
          setAiActive(false)
          setPaginationObj({})
          return
        }

        setAiSummary(summary)
        setAiActive(true)
        setRecommendations(data || [])

        const initialExpandedState = {}
        ;(data || []).forEach(group => { initialExpandedState[group.jobId] = true })
        setExpandedJobGroups(initialExpandedState)
        setPaginationObj({})
      }
    } catch (error) {
      console.error('AI search error:', error)
      setAiSummary(t('AI_ASSISTANT_UNAVAILABLE'))
      setAiActive(false)
    } finally {
      setAiLoading(false)
    }
  }

  // Follow-up search after the user picks a job from the clarification
  // chips. Sends the ORIGINAL query plus an explicit jobId so the backend
  // skips jobName resolution entirely and scopes to that one job, and
  // uses the real job name in the summary.
  const runAiSearchWithJob = async (queryText, jobId) => {
    const trimmed = (queryText ?? aiQuery).trim()
    if (!trimmed || aiLoading || !jobId) return

    setAiLoading(true)
    setAiClarification(null)
    setRecommendations([])
    setAiSummary(null)

    try {
      const result = await apiPost(apiPath.aiAssistantSearch, {
        query: trimmed,
        jobId
      })

      if (result?.status === 200 && result?.data?.success) {
        const { summary, data } = result.data.results
        setAiSummary(summary)
        setAiActive(true)
        setRecommendations(data || [])

        const initialExpandedState = {}
        ;(data || []).forEach(group => { initialExpandedState[group.jobId] = true })
        setExpandedJobGroups(initialExpandedState)
        setPaginationObj({})
      }
    } catch (error) {
      console.error('AI search error (with job):', error)
      setAiSummary(t('AI_ASSISTANT_UNAVAILABLE'))
      setAiActive(false)
    } finally {
      setAiLoading(false)
    }
  }

  // ── Drawer handlers ──
  const handleCandidateView = (candidate, jobGroup) => {
    setDrawerState({ mode: 'details', candidates: [candidate], jobGroup })
  }

  const handleCompareSelected = (candidates, jobGroup) => {
    setDrawerState({ mode: 'compare', candidates, jobGroup })
  }

  return (
    <div>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>

          {/* ── AI Search Box ── */}
          <div className='bg-white border border-[#E9EDF9] rounded-lg mb-3 p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <div className='w-7 h-7 rounded-full bg-LightBlue flex items-center justify-center'>
                <BsRobot className='text-white w-4 h-4' />
              </div>
              <span className='text-sm font-semibold text-gray-800'>{t('ASK_AI_TO_FIND_CANDIDATES')}</span>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); runAiSearch() }}
              className='flex items-center gap-2'
            >
              <input
                type='text'
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder={t('AI_SEARCH_PLACEHOLDER')}
                className='flex-1 p-2.5 text-sm rounded-lg bg-gray-50 border-2 border-[#DFDFDF] outline-none focus:border-LightBlue'
                disabled={aiLoading}
              />
              <button
                type='submit'
                disabled={aiLoading || !aiQuery.trim()}
                className='px-4 h-10 rounded-lg bg-LightBlue hover:bg-DarkBlue disabled:opacity-50 flex items-center gap-2 text-white text-sm flex-shrink-0'
              >
                {aiLoading ? <AiOutlineLoading3Quarters className='animate-spin w-4 h-4' /> : <BsSendFill className='w-4 h-4' />}
                {t('ASK')}
              </button>
              {aiActive && (
                <button
                  type='button'
                  onClick={clearAiSearch}
                  className='px-3 h-10 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-100 flex-shrink-0'
                >
                  {t('CLEAR')}
                </button>
              )}
            </form>

            {aiSummary && (
              <div className='mt-3 text-sm text-gray-700 bg-[#F3F5FA] rounded-lg px-3 py-2'>
                {aiSummary}
              </div>
            )}

            {aiClarification && aiClarification.options && aiClarification.options.length > 0 && (
              <div className='mt-3'>
                <div className='flex flex-wrap gap-2'>
                  {aiClarification.options.map((o) => (
                    <button
                      key={o.id}
                      type='button'
                      onClick={() => runAiSearchWithJob(aiQuery, o.id)}
                      className='text-xs px-3 py-1.5 rounded-full border border-LightBlue text-LightBlue hover:bg-LightBlue hover:text-white transition-colors'
                    >
                      {o.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3' onSubmit={(e) => e.preventDefault()}>
              <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                  <div className='flex items-center mr-3 mb-3'>
                    <select
                      id='jobSelect'
                      className='block p-2 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer min-w-[200px]'
                      onChange={handleJobChange}
                      value={selectedJob}
                    >
                      <option value=''>{t('ALL_JOBS')}</option>
                      {jobs.map(job => (
                        <option key={job._id} value={job._id}>{job.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className='relative flex items-center mb-3'>
                    <div className='absolute inset-y-0 right-0 flex items-center pl-3 mr-3 pointer-events-none'>
                      {!searchTerm && (
                        <svg aria-hidden='true' className='w-4 h-4 text-[#A5A5A5]' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                        </svg>
                      )}
                    </div>
                    <input
                      type='search'
                      id='default-search'
                      className='block w-full p-2 outline-none text-sm text-gray-900 2xl:min-w-[250px] xl:min-w-[300px] rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 border-2 border-[#DFDFDF]'
                      placeholder={'Search by name'}
                      value={searchTerm}
                      onChange={e => handleSearchTerm(e)}
                    />
                  </div>

                  <CustomSelect
                    id='sortColumn' name='sortColumn' options={columnOptions}
                    placeholder={t('SORT_BY_COLUMN')} value={sort.sortColumn}
                    onChange={handleSortColumnChange} t={t} extraClasses='ml-3 mb-3'
                  />

                  <CustomSelect
                    id='sortOrder' name='sortOrder' options={sortOptions}
                    placeholder={t('SORT_ORDER')} value={sort.sortType === 'asc' ? 'a_z' : 'z_a'}
                    onChange={handleSortOrderChange} t={t} extraClasses='ml-3 mb-3'
                  />

                  <button
                    type='button'
                    onClick={handleReset}
                    className='bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2'
                  >
                    {t('O_RESET')}
                  </button>
                </div>
              </div>
            </form>

            {(loading || aiLoading) ? (
              <div className='p-8 text-center'>
                <p className='mt-2 text-gray-600'>{t('LOADING_RECOMMENDATIONS')}</p>
              </div>
            ) : (
              <>
                {recommendations.length > 0 ? (
                  <div className='p-4'>
                    {recommendations.length > 1 && (
                      <div className='flex justify-end mb-4'>
                        <button type='button' onClick={toggleAllJobGroups} className='text-sm text-blue-600 hover:text-blue-800 flex items-center'>
                          {Object.values(expandedJobGroups).every(Boolean) ? t('COLLAPSE_ALL') : t('EXPAND_ALL')}
                        </button>
                      </div>
                    )}

                    <div className='space-y-4'>
                      {recommendations.map(jobGroup => {
                        const isExpanded = expandedJobGroups[jobGroup.jobId] || false
                        return (
                          <div key={jobGroup.jobId} className='border border-gray-200 rounded-lg overflow-hidden'>
                            <button
                              type='button'
                              onClick={() => toggleJobGroup(jobGroup.jobId)}
                              className='w-full p-4 bg-gray-50 hover:bg-gray-100 flex justify-between items-center transition-colors duration-200'
                            >
                              <div className='flex items-center'>
                                <div>
                                  <h3 className='text-sm font-semibold text-gray-800 text-left'>
                                    {jobGroup.jobName}
                                    <p className='text-xs text-gray-600 mt-1'>
                                      {jobGroup.totalCandidates}{jobGroup.totalCandidates > 1 ? ' Candidates' : ' Candidate'}
                                    </p>
                                  </h3>
                                </div>
                              </div>
                              <div className='flex items-center'>
                                <svg
                                  className={`w-4 h-4 text-gray-700 font-bold transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                  fill='none' stroke='currentColor' viewBox='0 0 24 24'
                                >
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                </svg>
                              </div>
                            </button>

                            {isExpanded && (
                              <div className='bg-white'>
                                <EmployeeList
                                  jobGroup={jobGroup}
                                  handleCandidateView={handleCandidateView}
                                  handleCompareSelected={handleCompareSelected}
                                  user={user}
                                  onBulkSelectDone={({ successCount, failedCount, jobGroup }) => {
                                    setBulkResult({ successCount, failedCount, jobName: jobGroup?.jobName })
                                    // ✅ Refresh the list so moved candidates no longer appear
                                    if (successCount > 0) {
                                      getRecommendations()
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div className='p-8 text-center'>
                    <p className='text-gray-600'>{t('NO_RECOMMENDATIONS_FOUND')}</p>
                  </div>
                )}
              </>
            )}

            {!aiActive && paginationObj?.totalItems && paginationObj.totalItems > pageSize ? (
              <Pagination handlePageClick={handlePageClick} options={paginationObj} page={page} />
            ) : null}
          </div>
        </div>
      </div>

      {drawerState && (
        <CandidateAiDrawer
          drawerState={drawerState}
          onClose={() => setDrawerState(null)}
        />
      )}
      {bulkResult && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">
              {t("CANDIDATES_MOVED_TO_INTERVIEW")}
            </h3>
            <p className="text-gray-600 mb-6">
              {bulkResult.successCount} {t("CANDIDATES_MOVED_SUCCESSFULLY")}{" "}
              <strong>{bulkResult.jobName}</strong>.
              {bulkResult.failedCount > 0 && (
                <> {bulkResult.failedCount} {t("CANDIDATES_FAILED_TO_MOVE")}.</>
              )}{" "}
              {t("PLEASE_SCHEDULE_THEIR_INTERVIEW_NEXT")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setBulkResult(null)}
                className="px-4 py-2 rounded text-sm bg-[#E1E1E1] hover:bg-gray-300"
              >
                {t("O_CLOSE")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkResult(null);
                  navigate("/interview-manager");
                }}
                className="px-4 py-2 rounded text-sm bg-LightBlue text-white hover:bg-DarkBlue"
              >
                {t("GO_TO_INTERVIEW_SCREEN")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeRecommendation